import { useMutation } from "@tanstack/react-query";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import type { PoolType } from "./useProductPools";

// UC-163 Work Item 4: Support adjustment by identifier (unlinked identifiers)
interface InvokePayload {
  organisation_id: string;
  master_product_id?: string | null;
  identifier_type?: string;
  identifier_value?: string;
  /** Ledger source key (e.g. manual_received, shopify_csv_import, shopify). pool_type is derived server-side. */
  source: string;
  quantity: number;
  effective_date: string;
  note?: string;
  confirm_negative: boolean;
}

export interface RecordAdjustmentPreview {
  preview: true;
  requires_confirmation: true;
  pool_type: PoolType;
  source?: string;
  transient?: boolean;
  current_balance: number;
  would_be_balance: number;
  delta: number;
}

export interface RecordAdjustmentSuccess {
  preview: false;
  event_id: string;
  pool_type: PoolType;
  source: string;
  transient?: boolean;
  before_quantity: number;
  delta: number;
  after_quantity: number;
}

export type RecordAdjustmentResult = RecordAdjustmentPreview | RecordAdjustmentSuccess;

async function invoke(payload: InvokePayload): Promise<RecordAdjustmentResult> {
  const { data, error } = await invokeFunction(supabase, "record-inventory-adjustment-v1", {
    body: payload,
  });
  if (error) throw new Error(error.message || "Failed to record adjustment");
  if (!data?.success) throw new Error(data?.error || "Failed to record adjustment");
  return data as RecordAdjustmentResult;
}

export function useRecordInventoryAdjustment() {
  const preview = useMutation({
    mutationFn: (input: Omit<InvokePayload, "confirm_negative">) =>
      invoke({ ...input, confirm_negative: false }),
  });
  const confirm = useMutation({
    mutationFn: (input: Omit<InvokePayload, "confirm_negative">) =>
      invoke({ ...input, confirm_negative: true }),
  });
  return { preview, confirm };
}
